import React, { useMemo } from 'react'
import ByAverageSpan from './ByAverageSpan'
import ByBin from './ByBin'
import ByIssuer from './ByIssuer'
import ByCardType from './ByCardType'
import ByPlatform from './ByPlatform'
import ByPlatformPerCardType from './ByPlatformPerCardType'
import ByReasonCodeDistribution from './ByReasonCodeDistribution'
import ByReasonCode from './ByReasonCode'
import ByTransactionAmount from './ByTransactionAmount'
import ByTransactionMonth from './ByTransactionMonth'
import ByLocationType from './ByLocationType'
import ByNonUniqueCustomer from './ByNonUniqueCustomer'
import ByTransactionDateWeek from './ByTransactionDateWeek'
import ByStatus from './ByStatus/ByStatus'
import ByWinLoss from './ByWinLoss/ByWinLoss'
import { IReportFilter } from '../Reports'
import { ReportsChartVariant } from '../../../../shared'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'

interface ReportsCharts {
    chartMap: ChartVariantMap<React.ReactElement>
    chartSectionKeys: string[]
}

interface ReportsChartsProps {
    filterValues: IReportFilter
    default_date_range: {
        [key: string]: {
            enabled: boolean
            value: number
        }
    }
}

type ChartVariantMap<Chart> = { [chart in ReportsChartVariant]: Chart }

const useReportsCharts = ({
    filterValues,
    // default_date_range,
}: ReportsChartsProps): ReportsCharts => {
    const { user } = useAuthedUser()
    const { CHARTS = false } = useFeatureToggle('REPORTS')

    const {
        merchant_id,
        mid_id,
        currency_id,
        date_reference,
        date_range,
        gateway_id,
        platforms,
    } = filterValues

    const filterValuesMemo = useMemo(
        () => filterValues,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            merchant_id,
            mid_id,
            currency_id,
            date_reference,
            date_range,
            gateway_id,
            platforms,
        ]
    )

    const chartMap: ChartVariantMap<React.ReactElement> = {
        reasonCodePctTop5Donut: (
            <ByReasonCode
                key={'key-reasonCodePctTop5Donut'}
                filterValues={filterValuesMemo}
            />
        ),
        cbByIssuerPctTop5Donut: (
            <ByIssuer
                key={'key-cbByIssuerPctTop5Donut'}
                filterValues={filterValuesMemo}
                topN={5}
            />
        ),
        cbByIssuerPctTop10Donut: (
            <ByIssuer
                key={'key-cbByIssuerPctTop10Donut'}
                filterValues={filterValuesMemo}
                topN={10}
            />
        ),
        cardTypeDonut: (
            <ByCardType
                key={'key-cardTypeDonut'}
                filterValues={filterValuesMemo}
            />
        ),
        reasonCodeDistributionDonut: (
            <ByReasonCodeDistribution
                key={'key-reasonCodeDistributionDonut'}
                filterValues={filterValuesMemo}
            />
        ),
        cbByTransMonthPctLine: (
            <ByTransactionMonth
                key={'key-cbByTransMonthPctLine'}
                filterValues={filterValuesMemo}
                // defaultDateRange={default_date_range?.cbByTransMonthPctLine}
            />
        ),
        cbByTransMonthPctArea: (
            <ByTransactionMonth
                key={'key-cbByTransMonthPctArea'}
                filterValues={filterValuesMemo}
            />
        ),
        cbByTransAmountPctBar: (
            <ByTransactionAmount
                key={'key-cbByTransAmountPctBar'}
                filterValues={filterValuesMemo}
            />
        ),
        averageSpanCbTransPostDatebyWeekPctLine: (
            <ByAverageSpan
                key={'key-averageSpanCbTransPostDatebyWeekPctLine'}
                filterValues={filterValuesMemo}
            />
        ),
        averageSpanCbTransPostDatebyWeekPctArea: (
            <ByAverageSpan
                key={'key-averageSpanCbTransPostDatebyWeekPctArea'}
                filterValues={filterValuesMemo}
            />
        ),
        cbByCardTypePerPlatformPctTop5Bar: (
            <ByPlatformPerCardType
                key={'key-cbByCardTypePerPlatformPctTop5Bar'}
                filterValues={filterValuesMemo}
            />
        ),
        cbByPlatformPctTop5Bar: (
            <ByPlatform
                key={'key-cbByPlatformPctTop5Bar'}
                filterValues={filterValuesMemo}
            />
        ),
        cbByBinPctTop5Bar: (
            <ByBin
                key={'key-cbByBinPctTop5Bar'}
                filterValues={filterValuesMemo}
                topN={5}
            />
        ),
        cbByBinPctTop10Bar: (
            <ByBin
                key={'key-cbByBinPctTop10Bar'}
                filterValues={filterValuesMemo}
                topN={10}
            />
        ),
        domesticVsOffshore: (
            <ByLocationType
                key={'key-domesticVsOffshore'}
                filterValues={filterValuesMemo}
            />
        ),
        cbTransRatioBytransDateWeekPctLine: (
            <ByTransactionDateWeek
                key={'key-cbTransRatioBytransDateWeekPctLine'}
                filterValues={filterValuesMemo}
                isLineChart
            />
        ),
        cbTransRatioBytransDateWeekPctArea: (
            <ByTransactionDateWeek
                key={'key-cbTransRatioBytransDateWeekPctArea'}
                filterValues={filterValuesMemo}
            />
        ),
        percentCBNonUniqueCustomer: (
            <ByNonUniqueCustomer
                key={'key-percentCBNonUniqueCustomer'}
                filterValues={filterValuesMemo}
            />
        ),
        cbByStatus: (
            <ByStatus key={'key-cbByStatus'} filterValues={filterValuesMemo} />
        ),
        cbByWinLoss: (
            <ByWinLoss
                key={'key-cbByWinLoss'}
                filterValues={filterValuesMemo}
            />
        ),
    }

    // returns section keys used to create sections in the reports view

    const chartSectionKeys =
        //@ts-ignore
        user?.role?.alias === 'merchant_admin'
            ? Object.keys(CHARTS.MERCHANT_ADMIN?.charts_and_position ?? {})
            : Object.keys(CHARTS.PARTNER_ADMIN?.charts_and_position ?? {})

    return {
        chartMap,
        chartSectionKeys,
    }
}

export default useReportsCharts
